import * as _ from 'underscore';

import * as React from 'react';
import { defaultMemoize } from 'reselect';

import {
  ActivitySpinnerSize, ActivitySpinnerWidget, TabActivationMode, TabAndPanelContents, TabListAlignment, TabStyles, TabsWidget
} from '@tableau/widgets';

import { CommonStyles } from '../../common/react/CommonStyles';
import { Location } from '../../common/react/Location';
import { SpaceContainerStyles } from '../../style/SpaceContainerStyles';
import { BrowserTitleService } from '../../ts/services/BrowserTitleService';
import { ServiceLocator } from '../ServiceLocator';
import { SubspaceName } from '../state/SpaceInfo';

export interface TabbedSpaceProps {
  readonly subspaces: ReadonlyArray<Subspace>;
  readonly activeSubspaceName: SubspaceName;
  readonly urlForSubspace: (subspaceName?: SubspaceName) => string;
  readonly mastheadElement: React.ReactNode;
  readonly testIdPrefix: string;
  readonly spaceItemName: string;
}

export type Subspace = {
  readonly name: SubspaceName,
  readonly tabLabel: string,
  readonly renderContent: () => JSX.Element,
  readonly shouldRenderTabContentCount: boolean;
  readonly tabContentCount?: number;
  readonly tabContentCountUpToDate?: boolean;
};

export const SpaceTabsTestId = 'space-tabs';

const HideTabPanelContent_WeManageItSeparately = <div style={{height: '0'}}></div>;

export function spaceTabButtonContent(subspace: Subspace, isActiveTab: boolean): JSX.Element {
  const fontFamilyForTab: string = isActiveTab ? CommonStyles.Fonts.HeavyFont: CommonStyles.Fonts.HeaderFont;
  return (
    <div className={SpaceContainerStyles.SpaceTabContainer} style={{fontFamily: fontFamilyForTab}}>
      {subspace.tabLabel}
      {subspace.shouldRenderTabContentCount &&
        <div className={SpaceContainerStyles.SpaceTabCountContainer} data-tb-test-id={'count-container'}>
          {subspace.tabContentCountUpToDate && subspace.tabContentCount}
          {!subspace.tabContentCountUpToDate &&
            <ActivitySpinnerWidget
              testId={'tab-waiting'}
              spinnerSize={ActivitySpinnerSize.Small} />
          }
        </div>
      }
    </div>
  );
}

function getTabs(subspaces: ReadonlyArray<Subspace>, activeSubspaceName: string): TabAndPanelContents[] {
  return subspaces.map(subspace => ({
    panelContent: HideTabPanelContent_WeManageItSeparately,
    tabContent: {
      // temporary hack until UX/Widgets can agree on what tabs should look like
      label: undefined,
      icon: spaceTabButtonContent(subspace, activeSubspaceName === subspace.name),
      title: subspace.tabLabel
    }
  }));
}

export class TabbedSpace extends React.PureComponent<TabbedSpaceProps> {

  private memoizedGetTabs = defaultMemoize(getTabs);
  private tabsWidgetRef: TabsWidget;
  private tabsDimensionOverrides = {
    tabHeight: SpaceContainerStyles.SpaceTabHeight,
    tabHorizontalPadding: TabStyles.Spacing.LargeSpace
  };

  public componentDidMount() {
    const activeIndex = this.getActiveSubspaceIndex();
    this.setBrowserTitle(this.props.subspaces[activeIndex]);
  }

  private getActiveSubspaceIndex(): number {
    const index = this.props.subspaces.findIndex(subspace => subspace.name === this.props.activeSubspaceName);
    return index >= 0 ? index : 0;
  }

  private renderTabs(): JSX.Element {
    const tabs = this.memoizedGetTabs(this.props.subspaces, this.props.activeSubspaceName);
    return (
      <TabsWidget
        activationMode={TabActivationMode.Manual}
        anchorTabIndex={0}
        ariaLabel={'Sub-spaces'}
        initialActiveIndex={this.getActiveSubspaceIndex()}
        onActiveIndexChanged={this.onTabIndexChanged}
        tabListAlignment={TabListAlignment.Left}
        tabs={tabs}
        testId={`${this.props.testIdPrefix}-${SpaceTabsTestId}`}
        ref={this.setTabsWidgetRef}
        dimensionOverrides={this.tabsDimensionOverrides}
      />
    );
  }

  private onTabIndexChanged = (index: number) => {
    const relevantSubspace = this.props.subspaces[index];
    const subspaceName = relevantSubspace.name;

    if (subspaceName !== this.props.activeSubspaceName) {
      const url = this.props.urlForSubspace(subspaceName);
      Location.instance.setUrl(url);
    }
  }

  private setBrowserTitle(subpsace: Subspace) {
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    browserTitleService.setTitleWithSpaceEntityNameAndTranslatedSubspaceName({
      spaceEntityName: this.props.spaceItemName,
      translatedSubspaceName: subpsace.tabLabel
    });
  }

  public render() {
    const activeSubspace = this.props.subspaces.find(s => s.name === this.props.activeSubspaceName);
    return (
      <div className={ SpaceContainerStyles.SpaceContainer }>
        <div className={ SpaceContainerStyles.MastheadArea }>
          { this.props.mastheadElement }
        </div>
        <div className={ SpaceContainerStyles.SpaceTabsContainer} >
          <div className={SpaceContainerStyles.SpaceTabsBottom} />
          {this.renderTabs()}
        </div>
        <div className={SpaceContainerStyles.SubplaceArea}>
          <div className={SpaceContainerStyles.SubplaceContainer}>
            {activeSubspace ? activeSubspace.renderContent() : null}
          </div>
        </div>
      </div>
    );
  }

  public componentDidUpdate(prevProps: TabbedSpaceProps) {
    if (this.tabsWidgetRef && prevProps.activeSubspaceName !== this.props.activeSubspaceName) {
      const activeIndex = this.getActiveSubspaceIndex();
      this.tabsWidgetRef.setActiveIndex(activeIndex);

      this.setBrowserTitle(this.props.subspaces[activeIndex]);
    }
  }

  private setTabsWidgetRef = (ref: TabsWidget) => {
    this.tabsWidgetRef = ref;
  }
}
